import type { NavData, NavGroup, NavItem, SearchableItem } from '../types';
import { IconRenderer } from '../services/IconRenderer';
import { ThemeManager } from '../services/ThemeManager';
import { tenantService } from '../services/TenantService';
import { oemConfig } from '../services/OEMConfig';
import { Logger } from './Logger';
import { fetchWithCacheBust } from '../utils/fetchUtils';

const logger = Logger.scope('SidebarBuilder');

/**
 * Represents a dynamic sidebar item that appears/disappears based on form navigation
 */
interface DynamicItemInfo {
  item: NavItem;
  groupTitle: string;
  element?: HTMLLIElement;
}

/**
 * SidebarBuilder - Handles building and rendering the sidebar navigation
 */
export class SidebarBuilder {
  private readonly iconRenderer = new IconRenderer();
  private searchableItems: SearchableItem[] = [];
  private dynamicItems: DynamicItemInfo[] = [];
  private currentForm: string = '';
  private navData?: NavData;

  constructor(
    private readonly containerId: string,
    private readonly themeManager: ThemeManager,
    private readonly onFormClick: (formName: string) => void
  ) {}

  /**
   * Build the sidebar from navigation data
   */
  public async build(): Promise<void> {
    const data = await this.fetchNavData();
    this.navData = data;
    const container = document.getElementById(this.containerId);
    
    if (!container) {
      logger.error(`Sidebar container ${this.containerId} not found`);
      return;
    }

    container.innerHTML = "";
    this.searchableItems = [];
    this.dynamicItems = [];

    // Render navigation groups (only static items initially)
    this.renderGroups(data.groups, container);

    // Render themes section
    this.renderThemes(container);

    // Wire up event handlers
    this.wireFormClicks();
    this.wireThemeClicks();

    // Listen for form load events to update dynamic items
    window.addEventListener('formLoaded', (event: Event) => {
      const customEvent = event as CustomEvent;
      this.updateDynamicItems(customEvent.detail.formName);
    });
  }

  /**
   * Get all searchable items
   */
  public getSearchableItems(): SearchableItem[] {
    return this.searchableItems;
  }

  /**
   * Fetch navigation data from tenant-specific JSON
   */
  private async fetchNavData(): Promise<NavData> {
    const navPath = tenantService.getFilePath('NavBar.json');
    logger.debug('Loading NavBar from:', navPath);
    
    const response = await fetchWithCacheBust(navPath);
    if (!response.ok) {
      throw new Error(`Failed to load NavBar: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Render navigation groups
   */
  private renderGroups(groups: NavGroup[], container: HTMLElement): void {
    for (const group of groups) {
      // Skip hidden groups (visible defaults to true)
      if (group.visible === false) {
        continue;
      }

      // Filter visible items and separate static from dynamic
      const visibleItems = group.items.filter(item => item.visible !== false);
      const staticItems = visibleItems.filter(item => !item.parentForm);
      const dynamicGroupItems = visibleItems.filter(item => item.parentForm);
      
      // Track dynamic items for later
      for (const item of dynamicGroupItems) {
        this.dynamicItems.push({
          item,
          groupTitle: group.title
        });
      }

      // Determine if group should be expanded (defaults to true)
      const isExpanded = group.expanded !== false;

      const li = document.createElement("li");
      li.setAttribute('data-group', group.title);
      li.innerHTML = `
        <details ${isExpanded ? 'open' : ''}>
          <summary>${group.title}</summary>
          <ul data-group-items="${group.title}">
            ${staticItems
              .map(
                (item) => `
              <li>
                <a
                  role="button"
                  ${item.disabled ? '' : `data-form="${item.form}"`}
                  ${item.disabled ? 'class="opacity-50 cursor-not-allowed"' : 'style="cursor: pointer;"'}
                  ${item.disabled ? 'aria-disabled="true"' : ''}
                >
                  ${this.iconRenderer.render(item.icon)}
                  <span>${item.label}</span>
                  ${item.badge ? `<span class="badge badge-xs badge-neutral ml-auto" style="flex: none;">${item.badge}</span>` : ''}
                </a>
              </li>`
              )
              .join("")}
          </ul>
        </details>
      `;
      container.appendChild(li);

      // Add static items to search index
      for (const item of staticItems) {
        this.searchableItems.push({
          label: item.label,
          form: item.form,
          group: group.title,
        });
      }
    }
  }

  /**
   * Render themes section
   * Only shown if OEM config allows user theme toggle
   */
  private renderThemes(container: HTMLElement): void {
    // Check if OEM allows user theme toggle
    const themeConfig = oemConfig.getTheme();
    if (!themeConfig.allowUserToggle) {
      logger.debug('Theme toggle disabled by OEM config');
      return;
    }

    const themes = this.themeManager.getThemes();
    const themesLi = document.createElement("li");
    
    // Check if themes should be expanded (default: true)
    const expanded = themeConfig.themesExpanded !== false;
    
    themesLi.innerHTML = `
      <details ${expanded ? 'open' : ''}>
        <summary>Themes</summary>
        <ul>
          ${themes
            .map(
              (t) => `
            <li>
              <button
                type="button"
                data-theme-item="${t}"
              >
                <div
                  data-theme="${t}"
                  class="bg-base-100 grid shrink-0 grid-cols-2 gap-0.5 rounded-md p-1 shadow-sm"
                >
                  <div class="bg-base-content size-2 rounded-full"></div>
                  <div class="bg-primary size-2 rounded-full"></div>
                  <div class="bg-secondary size-2 rounded-full"></div>
                  <div class="bg-accent size-2 rounded-full"></div>
                </div>
                <span>${t}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="theme-check h-3 w-3 opacity-0 shrink-0"
                >
                  <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"></path>
                </svg>
              </button>
            </li>`
            )
            .join("")}
        </ul>
      </details>
    `;
    
    container.appendChild(themesLi);
  }

  /**
   * Wire up form navigation clicks
   */
  private wireFormClicks(): void {
    document.querySelectorAll("[data-form]").forEach((a) => {
      a.addEventListener("click", (e) => {
        e.preventDefault();
        const formName = (e.currentTarget as HTMLElement).getAttribute("data-form")!;
        this.onFormClick(formName);

        // Auto-close drawer on mobile
        const drawer = document.getElementById("app-drawer") as HTMLInputElement | null;
        if (drawer && window.innerWidth < 1024) {
          drawer.checked = false;
        }
      });
    });
  }

  /**
   * Wire up theme button clicks
   */
  private wireThemeClicks(): void {
    document.querySelectorAll("[data-theme-item]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const theme = (e.currentTarget as HTMLElement).getAttribute("data-theme-item")!;
        this.themeManager.apply(theme);
      });
    });
  }

  /**
   * Update dynamic sidebar items based on the currently active form.
   * Dynamic items are shown only when their own form is active (not the parent).
   * They are hidden when navigating to any other form.
   * 
   * @param formName The form being navigated to
   */
  public updateDynamicItems(formName: string): void {
    this.currentForm = formName;
    logger.debug(`Updating dynamic items for form: ${formName}`);

    for (const dynamicInfo of this.dynamicItems) {
      const { item, groupTitle } = dynamicInfo;
      // Only show when the dynamic item's form itself is active
      const shouldShow = formName === item.form;

      if (shouldShow && !dynamicInfo.element) {
        // Create and show the dynamic item
        this.showDynamicItem(dynamicInfo);
      } else if (!shouldShow && dynamicInfo.element) {
        // Hide and remove the dynamic item
        this.hideDynamicItem(dynamicInfo);
      }
    }
  }

  /**
   * Show a dynamic sidebar item by inserting it after its parent form item
   */
  private showDynamicItem(dynamicInfo: DynamicItemInfo): void {
    const { item, groupTitle } = dynamicInfo;
    
    // Find the group's item list
    const groupItemsList = document.querySelector(`[data-group-items="${groupTitle}"]`);
    if (!groupItemsList) {
      logger.warn(`Could not find group items list for: ${groupTitle}`);
      return;
    }

    // Find the parent form's li element
    const parentFormLink = groupItemsList.querySelector(`[data-form="${item.parentForm}"]`);
    const parentLi = parentFormLink?.closest('li');
    
    if (!parentLi) {
      logger.warn(`Could not find parent form item: ${item.parentForm}`);
      return;
    }

    // Create the dynamic item element
    const li = document.createElement('li');
    li.setAttribute('data-dynamic-item', 'true');
    li.innerHTML = `
      <a
        role="button"
        data-form="${item.form}"
        style="cursor: pointer;"
        class="active"
      >
        ${this.iconRenderer.render(item.icon)}
        <span>${item.label}</span>
      </a>
    `;

    // Insert after the parent item
    parentLi.insertAdjacentElement('afterend', li);
    dynamicInfo.element = li;

    // Wire up click handler for this new element
    const anchor = li.querySelector('[data-form]');
    if (anchor) {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        const formName = (e.currentTarget as HTMLElement).getAttribute('data-form')!;
        this.onFormClick(formName);

        // Auto-close drawer on mobile
        const drawer = document.getElementById('app-drawer') as HTMLInputElement | null;
        if (drawer && window.innerWidth < 1024) {
          drawer.checked = false;
        }
      });
    }

    logger.debug(`Showed dynamic item: ${item.label}`);
  }

  /**
   * Hide and remove a dynamic sidebar item
   */
  private hideDynamicItem(dynamicInfo: DynamicItemInfo): void {
    if (dynamicInfo.element) {
      dynamicInfo.element.remove();
      dynamicInfo.element = undefined;
      logger.debug(`Hid dynamic item: ${dynamicInfo.item.label}`);
    }
  }
}
